/**
 * ONE CUSTOMER, FROM SIGNED TO RUNNING.
 *
 * The page this replaces was eleven sections down one scroll — contacts,
 * paperwork, contracts, billing, a checklist, the System Admin, reminders,
 * add-ons, single sign-on — with no order, no opinion about what mattered, and
 * two go-live checklists that could disagree with each other. It could be
 * operated by somebody who already knew the process and by nobody else.
 *
 * This is the same controls with a process around them: nine stages, one shown
 * at a time, each with a status computed from the records rather than ticked by
 * hand, and one sentence at the top saying what to do next.
 *
 * WHERE THE CONTROLS COME FROM. The stages that are genuinely new — Organization,
 * Contacts, Readiness, Go live — are written here. Agreements, Billing,
 * Authentication and Configuration mount the EXISTING panels unchanged, because
 * they carry two dozen controls between them and rewriting those would risk
 * losing one silently. They gain the workflow around them, not a rewrite.
 *
 * ONE READ. `sysAdminGetOnboarding` returns everything the stages need. The old
 * page made eight calls whose answers could disagree, and all three commercial
 * panels invalidated `['go-live']` — a key belonging to a different route — so
 * saving a contract never refreshed the checklist beside it.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { BackLink } from '../../../shared/BackLink';
import { call, isCallFailure } from '../../../lib/callable';
import { Spinner } from '../../../app/Spinner';
import { PageState } from '../../../shared/PageState';
import page from '../../../shared/Page.module.css';
import own from './Onboarding.module.css';

import { ContractsPanel } from '../ContractsPanel';
import { BillingPanel } from '../BillingPanel';
import { SsoSection } from '../SsoSection';
import { AddonsSection } from '../AddonsSection';

import { OnboardingHeader } from './OnboardingHeader';
import { NextActionPanel } from './NextActionPanel';
import { NeedsAttentionPanel } from './NeedsAttentionPanel';
import { StageNav, type View } from './StageNav';
import { StageStatusBadge } from './StageStatusBadge';
import { ContactMessage } from './ContactMessage';
import { StageNeeds } from './StageNeeds';
import { PaperworkPanel } from './PaperworkPanel';
import { JumpContext, useJumpState } from './jump';
import { RemindersSwitch } from './RemindersSwitch';
import { OrganizationStage } from './stages/OrganizationStage';
import { ContactsStage } from './stages/ContactsStage';
import { ReadinessStage } from './stages/ReadinessStage';
import { GoLiveStage } from './stages/GoLiveStage';
import { IntakeView } from './stages/IntakeView';
import { ActivityView } from './stages/ActivityView';
import { AdministratorsStage } from './stages/AdministratorsStage';
import type { Onboarding, StageId } from './types';

export function OnboardingPage() {
  const { orgId = '' } = useParams();
  const [view, setView] = useState<View>('organization');
  const [messageRole, setMessageRole] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['onboarding', orgId],
    queryFn: () => call<{ orgId: string }, Onboarding>('sysAdminGetOnboarding', { orgId }),
    // One retry: the first call of the day meets a cold container, and this is
    // the page's only read — losing it loses everything.
    retry: 1,
    retryDelay: 1000,
  });

  const go = (stage: StageId) => { setMessageRole(null); setView(stage); };
  const { jump, pending } = useJumpState(go);
  const contact = (role: string) => setMessageRole(role);

  if (query.isLoading) return <Spinner label="Loading this onboarding" />;

  if (query.error || !query.data) {
    return (
      <PageState title="Could not load this organization" tone="alert" action={{ to: '/console/orgs', label: 'All organizations' }}>
        {isCallFailure(query.error) ? query.error.message : 'It did not come back. Reload the page to try again.'}
      </PageState>
    );
  }

  const data = query.data;
  const stage = data.stages.find((s) => s.id === view);

  /*
   * No `page.body` wrapper. ConsoleShell's <main> already supplies the padding
   * and the 1180px cap; adding another inside it padded the content twice and
   * made every row start further in than the heading above it.
   */
  return (
    <>
      <div className={page.titled}>
        <BackLink to="/console/orgs" label="Back to all organizations" />
        <div className={page.titleLine}>
          <OnboardingHeader data={data} />
        </div>
      </div>

      <NextActionPanel data={data} onGo={go} onContact={contact} />
      <NeedsAttentionPanel data={data} onGo={go} />

      {messageRole && (
        <ContactMessage data={data} role={messageRole} onClose={() => setMessageRole(null)} />
      )}

      <JumpContext.Provider value={{ orgId, jump, pending }}>
      <div className={own.layout}>
        <StageNav stages={data.stages} current={view} onSelect={(v) => { setMessageRole(null); setView(v); }} />

        <div>
          {view === 'intake' && (
            <section className={page.section} aria-labeledby="view-title">
              <h2 id="view-title" className={page.sectionTitle}>Customer intake</h2>
              <p className={page.itemMeta}>Exactly what they submitted, and nothing we typed.</p>
              <IntakeView data={data} />
            </section>
          )}

          {view === 'activity' && (
            <section className={page.section} aria-labeledby="view-title">
              <h2 id="view-title" className={page.sectionTitle}>Activity</h2>
              <ActivityView orgId={orgId} />
            </section>
          )}

          {stage && (
            <section className={page.section} aria-labeledby="view-title">
              <div className={page.headerRow}>
                <h2 id="view-title" className={page.sectionTitle}>{stage.title}</h2>
                <StageStatusBadge status={stage.status} />
              </div>
              <p className={page.itemMeta}>{stage.blurb}</p>

              {view === 'organization' && (
                <OrganizationStage data={data} stage={stage} onViewIntake={() => setView('intake')} />
              )}
              {view === 'contacts' && (
                <ContactsStage data={data} stage={stage} onContact={contact} />
              )}
              {view === 'administrators' && (
                <AdministratorsStage data={data} stage={stage} />
              )}
              {view === 'readiness' && <ReadinessStage data={data} onGo={go} />}
              {view === 'goLive' && <GoLiveStage data={data} onGo={go} />}

              {/*
                * The four that mount an existing panel. Each keeps every control
                * it had; what is new is the computed requirement list above it,
                * which is what turns a form into a step.
                */}
              {view === 'agreements' && (
                <>
                  <StageNeeds requirements={stage.requirements} />
                  <ContractsPanel orgId={orgId} />
                  <PaperworkPanel orgId={orgId} paperwork={data.paperwork ?? {}} people={data.people} />
                </>
              )}
              {view === 'billing' && (
                <>
                  <StageNeeds requirements={stage.requirements} />
                  {data.suggestedPlan && !data.contract.exists && (
                    <div className={`${page.notice} ${page.noticeInfo}`}>
                      <strong>Suggested plan: {data.suggestedPlan}.</strong>{' '}
                      Based on the {data.intake?.locationCount} locations they gave us. A
                      suggestion only — nothing has been chosen and the price is still ours to set.
                    </div>
                  )}
                  <BillingPanel orgId={orgId} onEditContacts={() => go('contacts')} />
                </>
              )}
              {view === 'authentication' && (
                <>
                  <StageNeeds requirements={stage.requirements} />
                  {!data.sso.enabled ? (
                    <div className={`${page.notice} ${page.noticeInfo}`}>
                      <strong>They sign in with an emailed link.</strong> That is the answer for
                      every customer today: single sign-on is built but has never carried a real
                      sign-in and is not offered. There is nothing to configure here, and nothing
                      about it holds go-live.
                      <details style={{ marginTop: 'var(--sp-3)' }}>
                        <summary>Set up Microsoft Entra anyway</summary>
                        <SsoSection orgId={orgId} />
                      </details>
                    </div>
                  ) : (
                    <SsoSection orgId={orgId} />
                  )}
                </>
              )}
              {view === 'configuration' && (
                <>
                  <StageNeeds requirements={stage.requirements} />
                  <RemindersSwitch orgId={orgId} on={data.remindersOn} />
                  <AddonsSection orgId={orgId} />
                </>
              )}
            </section>
          )}
        </div>
      </div>
      </JumpContext.Provider>
    </>
  );
}
